

import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../style/Transaction.css';

const CodSummary = () => {
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(null); // Tracks row being confirmed

    const fetchCodSummaries = async () => {
        setLoading(true);
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));

            const summaryPromises = usersSnapshot.docs.map(async (userDoc) => {
                const userId = userDoc.id;
                const brandName = userDoc.data().brandName || 'Unnamed Brand';
                const codSummaryRef = collection(db, 'users', userId, 'codSummary');
                const codSummarySnapshot = await getDocs(codSummaryRef);

                return codSummarySnapshot.docs.map((docSnap) => {
                    const data = docSnap.data();
                    return {
                        userId,
                        date: docSnap.id,
                        brandName,
                        totalAmount: data.totalAmount,
                        lastUpdated: data.lastUpdated?.toDate().toLocaleString(),
                        status: data.status || 'pending',
                    };
                });
            });

            const results = await Promise.all(summaryPromises);
            setSummaryData(results.flat());
        } catch (error) {
            console.error('Error fetching COD summaries:', error);
            toast.error('Failed to fetch COD summary data.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (userId, date) => {
        const docRef = doc(db, 'users', userId, 'codSummary', date);
        setConfirming(`${userId}-${date}`);
        try {
            await updateDoc(docRef, { status: 'confirmed' });

            // Update local state
            setSummaryData((prev) =>
                prev.map((entry) =>
                    entry.userId === userId && entry.date === date
                        ? { ...entry, status: 'confirmed' }
                        : entry
                )
            );
            toast.success('Status updated to confirmed.');
        } catch (error) {
            console.error('Error confirming status:', error);
            toast.error('Failed to confirm status.');
        } finally {
            setConfirming(null);
        }
    };

    useEffect(() => {
        fetchCodSummaries();
    }, []);

    return (
        <div className="cod-summary-page">
            <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
            <h2>COD Summary</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <table className="cod-summary-table">
                    <thead>
                        <tr>
                            <th>Brand Name</th>
                            <th>Delivered Date</th>
                            <th>Total Amount (₹)</th>
                            <th>Remittance Date</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summaryData.length > 0 ? (
                            summaryData.map((entry, index) => (
                                <tr key={`${entry.userId}-${entry.date}-${index}`}>
                                    <td>{entry.brandName}</td>
                                    <td>{entry.date}</td>
                                    <td>{entry.totalAmount ? entry.totalAmount.toFixed(2) : 'NA'}</td>
                                    <td>{entry.lastUpdated || 'NA'}</td>
                                    <td>
                                        {entry.status === 'confirmed' ? (
                                            'Confirmed'
                                        ) : (
                                            <button
                                                onClick={() => handleConfirm(entry.userId, entry.date)}
                                                disabled={confirming === `${entry.userId}-${entry.date}`}
                                            >
                                                {confirming === `${entry.userId}-${entry.date}`
                                                    ? 'Confirming...'
                                                    : 'Confirm'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5">No COD summary data available.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default CodSummary;